import { zodResolver } from "@hookform/resolvers/zod";
import { PortableText } from "@portabletext/react";
import { Button, Combobox, DatePicker, ErrorContainer, Form, FormCheckbox, FormControl, FormField, FormInput, FormItem, FormLabel, FormMessage, Heading, IconLock, RadioGroup, RadioGroupItem, ScrollArea, Text } from "@sentricmusic/ui";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
// import { FaQuestionCircle } from "react-icons/fa";
import { toast } from "sonner";
import { z } from "zod";
// import RebrandButtonLink from "@/components/RebrandButtonLink";
// import useWriterFormTour from "@/components/tour/WriterFormTour";

import { useCreateWriter, useUpdateWriter } from "@/api/mutations";
import { useGetMyWriter, useGetMyWriters, useGetSocieties } from "@/api/queries";
import useSiteConfigAuth from "@/api/sanity/queries/getSiteConfigWithCode";
import useWritersTerms from "@/api/sanity/queries/getWritersTerms";
import validateIPNameNumber from "@/helpers/caeipiChecker.jsx";
import { nameRegexTest, publisherRegexTest } from "@/helpers/helpers";
import useAppStore from "@/store/appStateZustand";
import { useUIStateStore } from "@/store/uiStateStore";

import type { WriterForSelection } from "./types";

import ImroWritersForm from "../../writers/ImroWritersForm";

type WriterFormData = {
  firstname: string;
  lastname: string;
  middlename?: string;
  caeipinumber?: string;
  societycisaccode?: string;
  dateofbirth?: string;
  associatedPublisherName?: string;
  associatedPublisherCAEIPI?: string;
};

// Default empty array to avoid inline array creation
const DEFAULT_AVAILABLE_WRITERS: WriterForSelection[] = [];

type CreateWriterModalProps = {
  onWriterCreated: (writer: WriterForSelection) => void;
  onWriterUpdated?: (writer: WriterForSelection) => void;
  onClose: () => void;
  isUSPro?: boolean; // Legacy prop - now auto-detected from selected PRO
  editingWriterCode?: string | null;
  availableWriters?: WriterForSelection[];
};

export function CreateWriterModal({
  onWriterCreated,
  onWriterUpdated,
  onClose,
  isUSPro: _isUSPro = false, // Prefixed with _ to indicate intentionally unused
  editingWriterCode = null,
  availableWriters = DEFAULT_AVAILABLE_WRITERS,
}: CreateWriterModalProps) {
  const { t } = useTranslation(["songs", "common", "writers", "tutorial"]);
  const flags = useFlags();
  // const showTourFlag = Boolean(flags["pd-show-tutorial"]);
  const imroWriterSignUp = Boolean(flags["pd-imro-writer-registration"]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [showImroForm, setShowImroForm] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  const [hasAssociatedPublisher, setHasAssociatedPublisher] = useState<boolean>(false);
  const selectedAccount = useAppStore(s => s.selectedAccount);
  const isTunecore = useUIStateStore(state => state.isTunecore);

  // const startTour = useWriterFormTour();
  // const startWriterFormTour = () => {
  //   startTour();
  // };

  // Excluded IPI numbers for Tunecore
  const excludedIPINumbers = ["642005099", "641638348", "641080872"];

  // Create schema with translated error messages
  const writerFormSchema = z.object({
    firstname: z.string()
      .min(1, (t("regex:enter-first-name-check") ?? "First name is required"))
      .refine(val => nameRegexTest(val), {
        message: t("regex:invalid-characters2") ?? "Invalid characters in name",
      }),
    lastname: z.string()
      .min(1, (t("regex:enter-last-name-check") ?? "Last name is required"))
      .refine(val => nameRegexTest(val), {
        message: t("regex:invalid-characters2") ?? "Invalid characters in name",
      }),
    middlename: z.string().optional().refine(val => (val === undefined || val === "" || nameRegexTest(val)), {
      message: t("regex:invalid-characters2") ?? "Invalid characters in name",
    }),
    caeipinumber: z.string().optional().refine((val) => {
      if (val === undefined || val === "" || val.trim() === "")
        return true;

      // Must be at least 9 characters
      if (val.trim().length < 9) {
        return false;
      }

      // Tunecore-specific validations
      if (isTunecore) {
        // Check for excluded IPI numbers
        if (excludedIPINumbers.includes(val.trim())) {
          return false;
        }

        // Cannot start with "55"
        if (val.startsWith("55")) {
          return false;
        }
        // Validate checksum - validateIPNameNumber returns boolean
        // Type assertion needed due to missing .d.ts file for .jsx module
        const isValidChecksum = validateIPNameNumber(val) as unknown as boolean;
        return isValidChecksum;
      }

      return true;
    }, val => ({
      message: (() => {
        if (val === undefined || val === "" || val.trim() === "")
          return "";
        if (val.trim().length < 9)
          return t("regex:cae-ipi-regex") ?? "CAE/IPI must be at least 9 characters";

        if (isTunecore) {
          if (excludedIPINumbers.includes(val.trim())) {
            return t("regex:cae-ipi-excluded-regex") ?? "This CAE/IPI number is not allowed";
          }
          if (val.startsWith("55")) {
            return t("regex:cae-ipi-startswith-55-error") ?? "CAE/IPI cannot start with 55";
          }
          return t("regex:cae-ipi-checksum-error") ?? "Invalid CAE/IPI checksum";
        }

        return "";
      })(),
    })),
    societycisaccode: z.string().optional(),
    dateofbirth: z.string().optional().refine((val) => {
      if (val === undefined || val === "" || val.trim() === "")
        return true;

      // Parse the date string (format: YYYY-MM-DD from the calendar)
      const date = new Date(val);
      if (Number.isNaN(date.getTime())) {
        return false;
      }

      // Validate year range: not more than 100 years ago and not in the future
      const currentYear = new Date().getFullYear();
      const inputYear = date.getFullYear();
      if (inputYear < currentYear - 100 || inputYear > currentYear) {
        return false;
      }

      return true;
    }, {
      message: t("regex:invalid-date-of-birth-error") ?? "Invalid date of birth",
    }),
    associatedPublisherName: z.string().optional().refine(val => (val === undefined || val === "" || publisherRegexTest(val)), {
      message: t("regex:invalid-characters2") ?? "Invalid characters in publisher name",
    }),
    associatedPublisherCAEIPI: z.string().optional().refine((val) => {
      if (val === undefined || val === "" || val.trim() === "")
        return true;

      // Must be at least 9 characters
      if (val.trim().length < 9) {
        return false;
      }

      // For Tunecore, validate checksum
      if (isTunecore) {
        const isValidChecksum = validateIPNameNumber(val) as unknown as boolean;
        return isValidChecksum;
      }

      return true;
    }, val => ({
      message: (() => {
        if (val === undefined || val === "" || val.trim() === "")
          return "";
        if (val.trim().length < 9)
          return t("regex:cae-ipi-regex") ?? "CAE/IPI must be at least 9 characters";

        if (isTunecore) {
          return t("regex:cae-ipi-checksum-error") ?? "Invalid CAE/IPI checksum";
        }

        return "";
      })(),
    })),
  });

  const createWriterMutation = useCreateWriter();
  const updateWriterMutation = useUpdateWriter();
  const societiesQuery = useGetSocieties();

  // Get site config and writers data
  const { data: siteConfigData } = useSiteConfigAuth();
  const writersQuery = useGetMyWriters({ aac: selectedAccount as string });
  const writerTermsId = siteConfigData?.writer_terms?._id;
  // Sanity query lacks proper TypeScript definitions
  const { data: termsData = null } = useWritersTerms(writerTermsId ?? "") as { data: unknown };

  // US PRO detection - ASCAP, BMI, SESAC, AllTrack
  const usPros = ["010", "021", "071", "999"];

  // Check if we're in edit mode
  const isEditing = Boolean(editingWriterCode);

  // Get full writer data if editing
  const writerQuery = useGetMyWriter({
    aac: selectedAccount as string,
    wc: editingWriterCode ?? "",
  });

  // Find the writer being edited from availableWriters (limited data) or use API data (full data)
  const editingWriterFromAvailable = isEditing
    ? availableWriters.find(w => w.writercode === editingWriterCode)
    : null;

  // Use full writer data from API, fallback to limited data from availableWriters
  const editingWriter = isEditing ? (writerQuery.data ?? editingWriterFromAvailable) : null;

  const form = useForm<WriterFormData>({
    resolver: zodResolver(writerFormSchema),
    defaultValues: {
      firstname: "",
      lastname: "",
      middlename: "",
      caeipinumber: "",
      societycisaccode: "",
      dateofbirth: "",
      associatedPublisherName: "",
      associatedPublisherCAEIPI: "",
    },
  });

  // Watch the selected PRO to determine if it's a US PRO
  const selectedPRO = form.watch("societycisaccode");
  const isSelectedUSPro = selectedPRO != null && selectedPRO !== "" && selectedPRO !== "NO_PRO" && usPros.includes(selectedPRO);

  // Populate form when editing - immediate population from available data, then update with full data
  useEffect(() => {
    if (isEditing && editingWriterCode != null && editingWriterCode.trim() !== "") {
      // First, try to populate immediately with any available data
      const availableWriter = availableWriters.find(w => w.writercode === editingWriterCode);

      if (availableWriter) {
        // Parse the writer name to get firstname/lastname if we don't have them individually
        const writerName = availableWriter.writername ?? "";
        const nameParts = writerName.length > 0 ? writerName.split(" ") : ["", ""];
        const firstname = nameParts[0] ?? "";
        const lastname = nameParts.slice(1).join(" ");

        // Populate immediately with available data
        form.reset({
          firstname,
          lastname,
          middlename: "",
          caeipinumber: availableWriter.caeipinumber ?? "",
          societycisaccode: "",
          dateofbirth: availableWriter.dateofbirth ?? "",
          associatedPublisherName: "",
          associatedPublisherCAEIPI: "",
        });
      }
    }
  }, [isEditing, editingWriterCode, availableWriters, form]);

  // Update form with full API data when it becomes available
  useEffect(() => {
    if (isEditing && writerQuery.data) {
      const fullWriter = writerQuery.data;
      form.reset({
        firstname: fullWriter.firstname ?? "",
        lastname: fullWriter.lastname ?? "",
        middlename: fullWriter.middlename ?? "",
        caeipinumber: fullWriter.caeipinumber ?? "",
        societycisaccode: fullWriter.societycisaccode ?? "",
        dateofbirth: fullWriter.dateofbirth ?? "",
        associatedPublisherName: "", // Not available in API response
        associatedPublisherCAEIPI: "", // Not available in API response
      });
    }
  }, [isEditing, writerQuery.data, form]);

  // Clear form when switching from editing to creating or when no longer editing
  useEffect(() => {
    if (!isEditing) {
      form.reset({
        firstname: "",
        lastname: "",
        middlename: "",
        caeipinumber: "",
        societycisaccode: "",
        dateofbirth: "",
        associatedPublisherName: "",
        associatedPublisherCAEIPI: "",
      });
    }
  }, [isEditing, form]);

  const onFormSubmit = async (data: WriterFormData) => {
    setValidationErrors([]);

    try {
      // Custom validation rules
      const errors: string[] = [];

      // Task 5.1: Check terms acceptance for first writer creation
      if (!isEditing && (writersQuery.data?.writers.length ?? 0) === 0 && siteConfigData?.show_writer_terms === true) {
        if (!termsAccepted) {
          errors.push(t("regex:terms-and-conditions-error") ?? "You must accept the terms and conditions to continue");
        }
      }

      // Task 5.2: Validate CAE/IPI and PRO together (if one filled, both required)
      const hasCaeIpi = data.caeipinumber != null && data.caeipinumber.trim() !== "";
      const hasPro = data.societycisaccode != null && data.societycisaccode.trim() !== "" && data.societycisaccode.trim() !== "NO_PRO";

      if (hasCaeIpi && !hasPro) {
        errors.push(t("writers:pro-required-with-cae-ipi") ?? "A Performing Rights Organization (PRO) is required when a CAE/IPI number is provided");
      }
      else if (hasPro && !hasCaeIpi) {
        errors.push(t("writers:cae-ipi-required-with-pro") ?? "A CAE/IPI number is required when a Performing Rights Organization (PRO) is selected");
      }

      // Task 5.3: Validate associated publisher fields if hasAssociatedPublisher is true
      if (hasAssociatedPublisher) {
        const hasPublisherName = data.associatedPublisherName != null && data.associatedPublisherName.trim() !== "";
        const hasPublisherCaeIpi = data.associatedPublisherCAEIPI != null && data.associatedPublisherCAEIPI.trim() !== "";

        if (!hasPublisherName) {
          errors.push(t("regex:empty-publisher-name") ?? "Associated publisher name is required when 'Yes' is selected");
        }
        if (!hasPublisherCaeIpi) {
          errors.push(t("writers:publisher-cae-ipi-required") ?? "Associated publisher CAE/IPI is required when 'Yes' is selected");
        }
      }

      // If there are validation errors, display them and stop submission
      if (errors.length > 0) {
        setValidationErrors(errors);
        return;
      }

      // Handle date of birth formatting
      let formattedDateOfBirth = "";
      if (data.dateofbirth != null && data.dateofbirth.trim() !== "") {
        formattedDateOfBirth = data.dateofbirth.trim();
      }

      if (isEditing && editingWriter) {
        // UPDATE mode - need to include etag/version for optimistic concurrency control
        const updateData = {
          writercode: editingWriter.writercode,
          artistaccountcode: selectedAccount,
          firstname: data.firstname.trim(),
          lastname: data.lastname.trim(),
          middlename: (data.middlename != null && data.middlename.trim() !== "") ? data.middlename.trim() : "",
          caeipinumber: (data.caeipinumber != null && data.caeipinumber.trim() !== "") ? data.caeipinumber.trim() : "",
          societycisaccode: (data.societycisaccode != null && data.societycisaccode.trim() !== "" && data.societycisaccode.trim() !== "NO_PRO") ? data.societycisaccode.trim() : null,
          dateofbirth: formattedDateOfBirth,
          publishername: hasAssociatedPublisher ? ((data.associatedPublisherName != null && data.associatedPublisherName.trim() !== "") ? data.associatedPublisherName.trim() : "") : "",
          publishercaeipi: hasAssociatedPublisher ? ((data.associatedPublisherCAEIPI != null && data.associatedPublisherCAEIPI.trim() !== "") ? data.associatedPublisherCAEIPI.trim() : "") : "",
          isunknownwriter: false,
          // Include etag for optimistic concurrency control
          etag: (() => {
            if (editingWriter == null)
              return undefined;

            // Try to get etag from full Writer type first
            if ("etag" in editingWriter && editingWriter.etag != null) {
              return editingWriter.etag;
            }

            // Fallback to version field if available (from API response)
            const writerWithVersion = editingWriter as Record<string, unknown>;
            if (writerWithVersion.version != null) {
              return String(writerWithVersion.version);
            }

            return undefined;
          })(),
        };

        const result = await updateWriterMutation.mutateAsync(updateData);

        if (result?.failedvalidation === true) {
          // Handle validation failure
          if (result.messages && Array.isArray(result.messages)) {
            setValidationErrors(result.messages);
          }
          else {
            setValidationErrors([t("writers:update-failed-validation") ?? "Update failed due to validation errors"]);
          }
          return; // Don't close modal, let user fix the errors
        }

        if (result?.model) {
          // Create display name from firstname/lastname if writername is not available
          let displayName = result.model.writername;
          if (!displayName || displayName.trim() === "") {
            const parts = [result.model.firstname, result.model.middlename, result.model.lastname].filter(Boolean);
            displayName = parts.join(" ");
          }

          const updatedWriter: WriterForSelection = {
            writercode: result.model.writercode ?? "",
            writername: displayName ?? "",
            caeipinumber: result.model.caeipinumber ?? null,
            dateofbirth: result.model.dateofbirth ?? null,
            writerexternalregistrationstatus: (result.model.writerexternalregistrationstatus as string | undefined) ?? null,
          };

          // Call the onWriterUpdated callback with the updated writer
          onWriterUpdated?.(updatedWriter);
          toast.success(t("writers:writer-updated-success", { name: updatedWriter.writername }) ?? `Writer updated: ${updatedWriter.writername}`);
          onClose();
        }
        else {
          throw new Error(t("writers:failed-update-writer-no-model") ?? "Failed to update writer - no model returned");
        }
      }
      else {
        // CREATE mode
        const createData = {
          artistaccountcode: selectedAccount,
          firstname: data.firstname.trim(),
          lastname: data.lastname.trim(),
          middlename: (data.middlename != null && data.middlename.trim() !== "") ? data.middlename.trim() : "",
          caeipinumber: (data.caeipinumber != null && data.caeipinumber.trim() !== "") ? data.caeipinumber.trim() : "",
          societycisaccode: (data.societycisaccode != null && data.societycisaccode.trim() !== "" && data.societycisaccode.trim() !== "NO_PRO") ? data.societycisaccode.trim() : null,
          dateofbirth: formattedDateOfBirth,
          publishername: hasAssociatedPublisher ? ((data.associatedPublisherName != null && data.associatedPublisherName.trim() !== "") ? data.associatedPublisherName.trim() : "") : "",
          publishercaeipi: hasAssociatedPublisher ? ((data.associatedPublisherCAEIPI != null && data.associatedPublisherCAEIPI.trim() !== "") ? data.associatedPublisherCAEIPI.trim() : "") : "",
          isunknownwriter: false,
        };

        const result = await createWriterMutation.mutateAsync(createData);

        if (result?.failedvalidation === true) {
          // Handle validation failure
          if (result.messages && Array.isArray(result.messages)) {
            setValidationErrors(result.messages);
          }
          else {
            setValidationErrors([t("writers:creation-failed-validation") ?? "Creation failed due to validation errors"]);
          }
          return; // Don't close modal, let user fix the errors
        }

        if (result?.model) {
          // Create display name from firstname/lastname if writername is not available
          let displayName = result.model.writername;
          if (!displayName || displayName.trim() === "") {
            const parts = [result.model.firstname, result.model.middlename, result.model.lastname].filter(Boolean);
            displayName = parts.join(" ");
          }

          const newWriter: WriterForSelection = {
            writercode: result.model.writercode ?? "",
            writername: displayName ?? "",
            caeipinumber: result.model.caeipinumber ?? null,
            dateofbirth: result.model.dateofbirth ?? null,
            writerexternalregistrationstatus: (result.model.writerexternalregistrationstatus as string | undefined) ?? null,
          };

          // Call the onWriterCreated callback with the new writer
          onWriterCreated(newWriter);
          toast.success(t("writers:writer-created-success", { name: newWriter.writername }) ?? `Writer created: ${newWriter.writername}`);
          onClose();
        }
        else {
          throw new Error(t("writers:failed-create-writer-no-model") ?? "Failed to create writer - no model returned");
        }
      }
    }
    catch (error) {
      // Check if this is a validation error with messages
      if (error != null && typeof error === "object" && "message" in error) {
        try {
          const errorData = JSON.parse(error.message as string) as { messages?: string[] };
          if (errorData.messages != null && Array.isArray(errorData.messages)) {
            setValidationErrors(errorData.messages);
            return;
          }
        }
        catch {
          // Not JSON, continue to generic error
        }
      }

      toast.error(t("writers:failed-writer-operation", { operation: isEditing ? t("writers:update") ?? "update" : t("writers:create") ?? "create" }) ?? `Failed to ${isEditing ? "update" : "create"} writer. Please try again.`);
    }
  };

  // Handle IMRO writer creation success
  const handleImroWriterSuccess = (writerData: unknown) => {
    // Type guard to safely access writer properties
    const isWriterData = (data: unknown): data is Record<string, unknown> => {
      return typeof data === "object" && data !== null;
    };

    if (!isWriterData(writerData)) {
      toast.error(t("writers:invalid-writer-data") ?? "Invalid writer data received");
      return;
    }

    // Safely extract properties with proper fallbacks
    const writercode = typeof writerData.writercode === "string" ? writerData.writercode : "";
    const firstname = typeof writerData.firstname === "string" ? writerData.firstname : "";
    const lastname = typeof writerData.lastname === "string" ? writerData.lastname : "";
    const existingWritername = typeof writerData.writername === "string" ? writerData.writername : "";
    const writername = existingWritername || `${firstname} ${lastname}`.trim();
    const caeipinumber = typeof writerData.caeipinumber === "string" ? writerData.caeipinumber : null;
    const dateofbirth = typeof writerData.dateofbirth === "string" ? writerData.dateofbirth : null;
    const writerexternalregistrationstatus = typeof writerData.writerexternalregistrationstatus === "string"
      ? writerData.writerexternalregistrationstatus
      : null;

    const imroWriter: WriterForSelection = {
      writercode,
      writername,
      caeipinumber,
      dateofbirth,
      writerexternalregistrationstatus,
    };

    onWriterCreated(imroWriter);
    toast.success(t("writers:writer-created-success", { name: imroWriter.writername }) ?? `Writer created: ${imroWriter.writername}`);
    onClose();
  }; // If showing IMRO form, render it instead of the standard form
  if (showImroForm) {
    return (
      <ImroWritersForm
        onClose={() => setShowImroForm(false)}
        onSuccess={handleImroWriterSuccess}
        onBack={() => setShowImroForm(false)}
      />
    );
  }

  return (
    <div className="relative space-y-6">
      {/* Loading overlay for editing mode */}
      {isEditing && writerQuery.isLoading && (
        <div className="absolute inset-0 z-10 flex items-start justify-center pt-8">
          <Text as="p" className="px-4 py-2 bg-white rounded-md shadow-md">
            {t("writers:loading-writer-details") ?? "Loading writer details..."}
          </Text>
        </div>
      )}

      <div className={`space-y-4 ${isEditing && writerQuery.isLoading ? "blur-sm pointer-events-none" : ""}`}>
        <div className="flex items-center gap-2">
          <Heading as="h3">{isEditing ? t("writers:edit-writer") : t("writers:create-new-writer")}</Heading>
          {isEditing && editingWriter && "islocked" in editingWriter && editingWriter.islocked === true && (
            <IconLock className="w-5 h-5 stroke-sentric-mid-grey" />
          )}

          {/* {showTourFlag && (
            <RebrandButtonLink onClick={startWriterFormTour}>
              <div className="flex items-center gap-2">
                {t("tutorial:need-help")}
                {" "}
                <FaQuestionCircle className="inline mb-1 text-sentric-orange" />
              </div>
            </RebrandButtonLink>
          )} */}
        </div>
        {isEditing && editingWriter && "islocked" in editingWriter && editingWriter.islocked === true && (
          <Text as="p" className="font-bold">
            <span className="mr-1 text-sentric-orange">*</span>
            {t("writers:locked-field-explanation")}
          </Text>
        )}
        <Text as="p">{t("writers:legal-name-explanation")}</Text>

        {/* Display API validation errors */}
        {validationErrors.length > 0 && (
          <ErrorContainer
            validationIssues={validationErrors}
            title={validationErrors.length === 1 ? String(t("writers:validation-error")) : String(t("writers:validation-errors"))}
            variant="destructive"
            className="mt-4"
          />
        )}

        <Form {...form}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void form.handleSubmit(onFormSubmit)(e);
            }}
            className="space-y-4"
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">

              <FormField
                control={form.control}
                name="firstname"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>
                      {t("auth:first-name")}
                    </FormLabel>
                    <FormControl>
                      <FormInput
                        placeholder={t("auth:first-name") ?? ""}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="lastname"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>
                      {t("auth:last-name")}
                    </FormLabel>
                    <FormControl>
                      <FormInput
                        placeholder={t("auth:last-name") ?? ""}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="middlename"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("auth:middle-name")}</FormLabel>
                    <FormControl>
                      <FormInput
                        placeholder={t("auth:middle-name") ?? ""}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="caeipinumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("writers:cae-ipi-number")}</FormLabel>
                    <FormControl>
                      <FormInput
                        placeholder={t("writers:cae-ipi-number-placeholder") ?? ""}
                        {...field}
                        value={field.value ?? ""}
                      />

                    </FormControl>

                    {/* IMRO Registration Link - only show when creating (not editing) and feature flag is on */}
                    {!isEditing && imroWriterSignUp && (
                      <Button
                        variant="link"
                        className="mt-1 underline"
                        onClick={() => setShowImroForm(true)}

                        label={t("writers:imro-writer-create") ?? "No IPI Number? Register with IMRO."}
                      />
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {/* <FormField
                control={form.control}
                name="societycisaccode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Society</FormLabel>
                    <FormControl>
                      <Select
                        value={(field.value != null && field.value !== "") ? field.value : "NO_PRO"}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger className="w-full pl-0">
                          <SelectValue placeholder={societiesQuery.isLoading ? t("writers:loading-pros") : t("writers:select-writer-pro")} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="NO_PRO">
                            {t("writers:no-pro")}
                          </SelectItem>
                          {societiesQuery.data?.societies?.map((society: { cisaccode: string; name: string }) => (
                            <SelectItem key={society.cisaccode} value={society.cisaccode}>
                              {society.name}
                              {" "}
                              (
                              {society.cisaccode}
                              )
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              /> */}

              {/* A/B Testing: New Combobox version of Society selector */}
              <FormField
                control={form.control}
                name="societycisaccode"
                render={({ field }) => {
                  // Transform societies data for combobox
                  const societyOptions = [
                    { value: "NO_PRO", label: String(t("writers:no-pro")) },
                    ...(societiesQuery.data?.societies?.map((society: { cisaccode: string; name: string }) => ({
                      value: society.cisaccode,
                      label: `${society.name} (${society.cisaccode})`,
                    })) ?? []),
                  ];

                  return (
                    <FormItem>
                      <FormLabel>{t("writers:writer-pro-name")}</FormLabel>
                      <FormControl>
                        <Combobox
                          data={societyOptions}
                          value={(field.value != null && field.value !== "") ? field.value : ""}
                          onValueChange={field.onChange}
                          placeholder={t("writers:choose-organisation-placeholder") ?? ""}
                          searchPlaceholder={t("writers:choose-organisation-placeholder") ?? ""}
                          emptyText={t("writers:no-pro-found") ?? "No PRO found."}
                          className="w-full"
                          searchByLabel={true}
                          disablePortal={true}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />

              <FormField
                control={form.control}
                name="dateofbirth"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("writers:date-of-birth")}</FormLabel>
                    <FormControl>
                      <DatePicker
                        placeholder={t("writers:date-format-placeholder") ?? "DD/MM/YYYY"}
                        value={field.value != null && field.value.trim() !== "" ? new Date(field.value) : undefined}
                        onChange={(date: Date | undefined) => {
                          if (date) {
                            // Format as YYYY-MM-DD in local timezone
                            const year = date.getFullYear();
                            const month = String(date.getMonth() + 1).padStart(2, "0");
                            const day = String(date.getDate()).padStart(2, "0");
                            const formattedDate = `${year}-${month}-${day}`;
                            field.onChange(formattedDate);
                          }
                          else {
                            field.onChange("");
                          }
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Associated Publisher Radio - Show only for US PROs */}
            {isSelectedUSPro && (
              <div className="pt-4 space-y-3">
                <RadioGroup
                  value={hasAssociatedPublisher ? "yes" : "no"}
                  onValueChange={(value) => {
                    setHasAssociatedPublisher(value === "yes");
                    // Clear publisher fields if "No" is selected
                    if (value === "no") {
                      form.setValue("associatedPublisherName", "");
                      form.setValue("associatedPublisherCAEIPI", "");
                    }
                  }}
                >
                  <label className="">
                    <Text>{t("writers:associated-publisher-confirm")}</Text>

                  </label>
                  <div className="flex items-center gap-4 mt-2">
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="yes" id="publisher-yes" />
                      <label
                        htmlFor="publisher-yes"
                        className="cursor-pointer"
                      >
                        <Text>
                          {t("common:yes") ?? "Yes"}
                        </Text>
                      </label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="no" id="publisher-no" />
                      <label
                        htmlFor="publisher-no"
                        className="cursor-pointer"
                      >
                        <Text>
                          {t("common:no") ?? "No"}
                        </Text>
                      </label>
                    </div>
                  </div>
                </RadioGroup>
              </div>
            )}

            {/* Associated Publisher Fields - Show only when US PRO AND user selected "Yes" */}
            {isSelectedUSPro && hasAssociatedPublisher && (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="associatedPublisherName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("writers:associated-publisher-name")}</FormLabel>
                      <FormControl>
                        <FormInput
                          placeholder={t("writers:associated-publisher-name-placeholder") ?? ""}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="associatedPublisherCAEIPI"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("writers:associated-publisher-cae-ipi")}</FormLabel>
                      <FormControl>
                        <FormInput
                          placeholder={t("writers:associated-publisher-cae-ipi-number-placeholder") ?? ""}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {/* Terms & Conditions Section - Show only for first writer creation when flag is enabled */}
            {!isEditing
              && (writersQuery.data?.writers.length ?? 0) === 0
              && siteConfigData?.show_writer_terms === true
              && termsData != null && (
              <div className="pt-4">
                <Text>
                  {t("common:terms")}
                  {" "}
                  &amp;
                  {" "}
                  {t("common:conditions")}
                </Text>
                <ScrollArea className="h-64 p-4 border rounded-md border-sentric-mid-grey ">
                  <div className="pr-4 prose-sm prose dark:prose-invert max-w-none">
                    <PortableText value={(termsData as Record<string, unknown>)?.terms as Parameters<typeof PortableText>[0]["value"]} />
                  </div>
                </ScrollArea>
                <div className="mt-4">
                  <FormCheckbox
                    id="terms-acceptance"
                    checked={termsAccepted}
                    onCheckedChange={setTermsAccepted}
                    label={t("writers:i-have-read-terms-and-conditions") ?? "I ACCEPT the terms and conditions of the 28 Day Licence Agreement and warrant that I am authorised to do so on behalf of the applicable Writer (in the event I am not the individual Writer)."}
                  />
                </div>
              </div>
            )}

            <div className="flex flex-row justify-between w-full pt-4">
              <Button
                type="button"
                variant="outline"
                label={t("common:cancel")}
                onClick={onClose}
                disabled={isEditing ? updateWriterMutation.isPending : createWriterMutation.isPending}
              />
              <Button
                type="submit"
                variant="secondary"
                disabled={
                  isEditing
                    ? updateWriterMutation.isPending
                    : (createWriterMutation.isPending
                      || (
                        // Disable if terms need to be accepted but haven't been
                        (writersQuery.data?.writers.length ?? 0) === 0
                        && siteConfigData?.show_writer_terms === true
                        && termsData != null
                        && !termsAccepted
                      ))
                }
                label={
                  isEditing
                    ? (updateWriterMutation.isPending ? t("writers:updating-writer") ?? "" : t("writers:update-writer-button") ?? "")
                    : (createWriterMutation.isPending ? t("writers:adding-writer") ?? "" : t("writers:add-writer-button") ?? "")
                }
              />
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}
